import { useForm } from "@tanstack/react-form";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { requestPasswordReset } from "@/auth/password";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { forgotPasswordSchema } from "@/lib/schemas";

export const Route = createFileRoute("/_public/esqueci-senha")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: { email: "" },
    validators: { onSubmit: forgotPasswordSchema },
    onSubmit: async ({ value }) => {
      setFormError(null);
      const result = await requestPasswordReset(value.email);
      if (result.ok) {
        setSent(true);
        return;
      }
      setFormError(
        result.reason === "rate_limited"
          ? "Muitas tentativas. Aguarde um instante e tente de novo."
          : "Não foi possível enviar o e-mail. Tente novamente.",
      );
    },
  });

  // A API responde 200 genérico exista ou não a conta, justamente para não
  // virar um enumerador de e-mails cadastrados. A tela mantém esse contrato:
  // a confirmação é a mesma nos dois casos.
  if (sent) {
    return (
      <FieldGroup>
        <div className="flex flex-col gap-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Confira seu e-mail</h1>
          <p className="text-sm text-muted-foreground">
            Se houver uma conta com esse endereço, enviamos um link para você criar uma
            nova senha. O link vale por tempo limitado.
          </p>
        </div>
        <FieldDescription className="text-center">
          <Link
            to="/login"
            search={{ redirect: "/" }}
            className="underline underline-offset-4"
          >
            Voltar para o login
          </Link>
        </FieldDescription>
      </FieldGroup>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <FieldGroup>
        <div className="flex flex-col gap-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Recuperar senha</h1>
          <p className="text-sm text-muted-foreground">
            Enviaremos um link para você criar uma nova senha.
          </p>
        </div>

        <form.Field name="email">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={field.name}>E-mail</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                type="email"
                autoComplete="email"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldError errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>

        {formError && (
          <p role="alert" className="text-sm text-destructive">
            {formError}
          </p>
        )}

        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Field>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Enviando…" : "Enviar link"}
              </Button>
            </Field>
          )}
        </form.Subscribe>

        <FieldDescription className="text-center">
          Lembrou a senha?{" "}
          <Link
            to="/login"
            search={{ redirect: "/" }}
            className="underline underline-offset-4"
          >
            Entrar
          </Link>
        </FieldDescription>
      </FieldGroup>
    </form>
  );
}
